import { ref, onBeforeUnmount } from "vue";
import router from "@/router";
import { auth, signOut } from "@/firebase";
import { logDebug, logInfo, logWarn, logError } from "@/utils/secureLogger";

interface UseInactivityTimeoutOptions {
  timeoutMs?: number;
  onTimeout?: () => Promise<void> | void;
  getStaySignedInFlag?: () => boolean;
}

const DEFAULT_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "visibilitychange",
];

const isBrowser = typeof window !== "undefined" && typeof document !== "undefined";

const getStoredStaySignedIn = (): boolean => {
  if (!isBrowser) {
    return false;
  }

  const sources = [
    localStorage.getItem("staySignedIn"),
    localStorage.getItem("rememberMe"),
    sessionStorage.getItem("staySignedIn"),
    sessionStorage.getItem("rememberMe"),
  ];

  return sources.some((value) => value === "true");
};

export const performSecureLogout = async (reason: "inactivity" | "manual" = "manual") => {
  logInfo("Performing secure logout", { reason });

  try {
    await signOut(auth);
  } catch (error) {
    logWarn("Firebase signOut failed during logout", error instanceof Error ? error : new Error(String(error)));
  }

  if (isBrowser) {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("authToken");
      localStorage.removeItem("userRole");
      localStorage.removeItem("rememberMe");
      localStorage.removeItem("staySignedIn");
      localStorage.removeItem("deviceTrusted");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("rememberMe");
      sessionStorage.removeItem("staySignedIn");
    } catch (error) {
      logWarn("Failed to clear stored auth artifacts during logout", error instanceof Error ? error : new Error(String(error)));
    }
  }

  try {
    router.replace({ name: "login" });
  } catch (error) {
    logError("Router redirect failed during logout", error instanceof Error ? error : new Error(String(error)));
  }
};

export function useInactivityTimeout(options: UseInactivityTimeoutOptions = {}) {
  const timeoutId = ref<number | null>(null);
  const isTracking = ref(false);
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const resolveStaySignedIn =
    options.getStaySignedInFlag ??
    (() => {
      return getStoredStaySignedIn();
    });

  const clearTimer = () => {
    if (timeoutId.value !== null) {
      window.clearTimeout(timeoutId.value);
      timeoutId.value = null;
    }
  };

  const stopTracking = () => {
    if (!isBrowser) {
      return;
    }

    clearTimer();
    if (isTracking.value) {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity, true);
      });
      isTracking.value = false;
      logDebug("Inactivity tracking stopped");
    }
  };

  const handleTimeout = async () => {
    stopTracking();

    if (options.onTimeout) {
      try {
        await options.onTimeout();
      } catch (error) {
        logError("Custom timeout handler failed", error instanceof Error ? error : new Error(String(error)));
      }
    } else {
      await performSecureLogout("inactivity");
    }
  };

  const scheduleTimer = () => {
    clearTimer();
    timeoutId.value = window.setTimeout(handleTimeout, timeoutMs);
  };

  const handleActivity = (event: Event) => {
    if (!isTracking.value) {
      return;
    }

    if (event.type === "visibilitychange" && document.visibilityState === "hidden") {
      return;
    }

    scheduleTimer();
  };

  const startTracking = () => {
    if (!isBrowser) {
      logWarn("Inactivity tracking skipped - non-browser environment");
      return;
    }

    if (resolveStaySignedIn()) {
      logInfo("Stay signed in enabled - skipping inactivity timeout");
      stopTracking();
      return;
    }

    if (isTracking.value) {
      logDebug("Inactivity tracking already active - resetting timer");
      scheduleTimer();
      return;
    }

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, true);
    });

    isTracking.value = true;
    scheduleTimer();
    logInfo("Inactivity tracking started", { timeoutMs });
  };

  onBeforeUnmount(() => {
    stopTracking();
  });

  return {
    startTracking,
    stopTracking,
    isTracking,
    refreshTimer: () => {
      if (isTracking.value) {
        scheduleTimer();
      }
    },
  };
}

